import React, { useMemo, useState } from 'react';
import {
  Card,
  Button,
  Space,
  Tag,
  Typography,
  Descriptions,
  Table,
  Spin,
  Modal,
  Form,
  Input,
  App,
  Alert,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExperimentOutlined,
  PrinterOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { qualityService, type InspectionRecord } from '@/services/quality.service';
import { MtcDocumentLink } from '@/components/documents/MtcDocumentLink';
import InspectionSamplesCard from '@/components/quality/InspectionSamplesCard';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { ENTITY_TYPE_RECEIVING_INSPECTION } from '@/constants/approval-entity-types';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;

type HeatLineRow = {
  id: string;
  grn_item_id: string;
  line_number?: number;
  item_code?: string | null;
  item_description?: string | null;
  heat_number: string;
  dimension?: string;
  quantity: number;
  mtc_document_id?: string | null;
  mtc_document_number?: string | null;
};

type ReceivingDetailRow = Record<string, unknown> & {
  id: string;
  grn_item_id?: string | null;
  item_code?: string | null;
  po_line_number?: number | null;
  remarks?: string;
  heat_lines?: HeatLineRow[];
};

type DimensionalSummary = {
  id: string;
  inspection_number: string;
  status: string;
  result: string;
  findings?: string;
  scheduled_date?: string;
  actual_date?: string;
};

type ReceivingInspection = InspectionRecord & {
  inspection_category?: string;
  source_document_type?: string | null;
  source_document_id?: string | null;
  observations?: string;
  lot_quantity?: number | null;
  sample_size?: number | null;
  sampling_plan?: string | null;
  samples?: Array<{
    id?: string;
    receiving_inspection_detail_id?: string | null;
    sample_number?: number;
    sample_qty?: number;
    status?: string;
    notes?: string | null;
  }>;
  receiving_details?: ReceivingDetailRow[];
  heat_lines?: HeatLineRow[];
  dimensional_inspection?: DimensionalSummary | null;
  dimensional_bypass_reason?: string | null;
  dimensional_bypass_at?: string | null;
  grn?: {
    id: string;
    grn_number: string;
    grn_status: string;
    inspection_status: string;
    inspection_required: boolean;
  };
};

const ReceivingInspectionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [completeOpen, setCompleteOpen] = useState(false);
  const [completeResult, setCompleteResult] = useState<'Pass' | 'Fail'>('Pass');
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [initiating, setInitiating] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['receiving-inspection', id],
    queryFn: () => qualityService.getInspectionById(id!) as Promise<ReceivingInspection>,
    enabled: !!id,
  });

  const canComplete =
    data &&
    data.status !== 'Completed' &&
    data.status !== 'Cancelled' &&
    data.status !== 'Pending Approval' &&
    (data.result === 'Pending' || !data.result);

  const showApproval = Boolean(
    data?.approval_instance_id || data?.status === 'Pending Approval'
  );

  const dim = data?.dimensional_inspection;
  const dimInitiated = Boolean(data?.dimensional_inspection_id || dim?.id);
  const dimPassed = dim?.status === 'Completed' && dim?.result === 'Pass';
  const dimFailed = dim?.result === 'Fail' || (dim?.status === 'Completed' && dim?.result === 'Fail');

  const heatLines = useMemo(() => data?.heat_lines || [], [data?.heat_lines]);
  const hasMtcGap = useMemo(
    () => heatLines.some((h) => !String(h.mtc_document_id || '').trim()),
    [heatLines]
  );

  const openComplete = (result: 'Pass' | 'Fail') => {
    if (result === 'Pass' && dimInitiated && !dimPassed) {
      message.error(
        dimFailed
          ? 'Receiving Dimension Inspection failed — Pass Dimension (or re-initiate after Fail) before Passing Receiving'
          : 'Receiving Dimension Inspection must be Passed before Passing Receiving'
      );
      return;
    }
    setCompleteResult(result);
    form.setFieldsValue({
      findings: data?.findings || '',
      dimensional_bypass_reason: '',
    });
    setCompleteOpen(true);
  };

  const initiateDimension = async () => {
    if (!id) return;
    setInitiating(true);
    try {
      const child = await qualityService.initiateReceivingDimensionalInspection(id);
      message.success(`Dimension Inspection ${child.inspection_number} initiated`);
      await refetch();
      queryClient.invalidateQueries({ queryKey: ['inspections'] });
      navigate(`/qms/receiving-dimensional-inspections/${child.id}`);
    } catch (e: unknown) {
      message.error((e as Error)?.message || 'Failed to initiate Dimension Inspection');
    } finally {
      setInitiating(false);
    }
  };

  const submitComplete = async () => {
    const values = await form.validateFields();
    if (completeResult === 'Fail' && !String(values.findings || '').trim()) {
      message.error('Findings are required when failing inspection');
      return;
    }
    if (
      completeResult === 'Pass' &&
      !dimInitiated &&
      !String(values.dimensional_bypass_reason || '').trim()
    ) {
      message.error('Bypass reason is required when Dimension Inspection was not initiated');
      return;
    }
    setSaving(true);
    try {
      await qualityService.submitInspectionForApproval(id!, {
        result: completeResult,
        findings: values.findings,
        dimensional_bypass_reason:
          completeResult === 'Pass' && !dimInitiated
            ? String(values.dimensional_bypass_reason || '').trim()
            : undefined,
      });
      message.success(
        completeResult === 'Pass'
          ? 'Pass submitted for approval — GRN Post unlocks after approval'
          : 'Fail submitted for approval'
      );
      setCompleteOpen(false);
      await refetch();
      queryClient.invalidateQueries({ queryKey: ['inspections'] });
      queryClient.invalidateQueries({ queryKey: ['grn'] });
    } catch (e: unknown) {
      message.error((e as Error)?.message || 'Failed to submit for approval');
    } finally {
      setSaving(false);
    }
  };

  if (isLoading || !data) {
    return <Spin style={{ display: 'block', margin: '48px auto' }} />;
  }

  const details = data.receiving_details || [];

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/scm/procurement/receiving-inspections')}>
          Back to receiving inspections
        </Button>
        {id && (
          <Button
            icon={<PrinterOutlined />}
            onClick={() => window.open(`/print/scm/procurement/receiving-inspections/${id}`, '_blank')}
          >
            Print
          </Button>
        )}
      </Space>

      <Title level={3} style={{ marginTop: 0 }}>
        Receiving Inspection {data.inspection_number}
      </Title>
      <Text type="secondary">GRN receiving QC — submit Pass/Fail for approval to unlock warehouse Post</Text>

      {data.status === 'Pending Approval' && (
        <Alert
          style={{ marginTop: 16, marginBottom: 16 }}
          type="info"
          showIcon
          message="Pending approval"
          description={`Awaiting approval of ${data.pending_result || 'result'}. Use the Approval panel or Approval Inbox.`}
        />
      )}

      {showApproval && id && (
        <Card title="Approval" size="small" style={{ marginTop: 16, marginBottom: 16 }}>
          <ApprovalPanel
            entityType={ENTITY_TYPE_RECEIVING_INSPECTION}
            entityId={id}
            extraInvalidateQueryKeys={[['receiving-inspection', id], ['inspections'], ['grn']]}
          />
        </Card>
      )}

      {data.grn && (
        <Alert
          style={{ marginTop: 16, marginBottom: 16 }}
          type="info"
          showIcon
          message={
            <Space wrap>
              <span>
                Linked GRN: <strong>{data.grn.grn_number}</strong> ({data.grn.grn_status}) · QC{' '}
                {data.grn.inspection_status}
              </span>
              <Link to={`/scm/procurement/grn/${data.grn.id}`}>Open GRN (view)</Link>
            </Space>
          }
        />
      )}

      <Card size="small" style={{ marginBottom: 16 }}>
        <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small">
          <Descriptions.Item label="Status">
            <Tag>{data.status}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Result">
            <Tag color={data.result === 'Pass' ? 'green' : data.result === 'Fail' ? 'red' : 'default'}>
              {data.result}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Category">{data.inspection_category || 'Receiving'}</Descriptions.Item>
          <Descriptions.Item label="Scheduled">
            {data.scheduled_date ? dayjs(data.scheduled_date).format('DD/MM/YYYY') : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Actual">
            {data.actual_date ? dayjs(data.actual_date).format('DD/MM/YYYY') : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Hold point">
            {data.is_hold_point ? 'Yes' : 'No'}
          </Descriptions.Item>
        </Descriptions>
        {data.observations && (
          <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            {data.observations}
          </Text>
        )}
        {data.dimensional_bypass_reason && (
          <Alert
            style={{ marginTop: 12 }}
            type="warning"
            showIcon
            message="Dimension bypass recorded"
            description={
              <>
                {data.dimensional_bypass_reason}
                {data.dimensional_bypass_at
                  ? ` · ${dayjs(data.dimensional_bypass_at).format('DD/MM/YYYY HH:mm')}`
                  : ''}
              </>
            }
          />
        )}
        {canComplete && (
          <Space style={{ marginTop: 16 }}>
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={() => openComplete('Pass')}
              disabled={Boolean(dimInitiated && !dimPassed)}
            >
              Pass — submit for approval
            </Button>
            <Button danger icon={<CloseCircleOutlined />} onClick={() => openComplete('Fail')}>
              Fail — submit for approval
            </Button>
          </Space>
        )}
      </Card>

      {id && (
        <InspectionSamplesCard
          inspectionId={id}
          canEdit={Boolean(canComplete)}
          lotQuantity={data.lot_quantity}
          sampleSize={data.sample_size}
          samplingPlan={data.sampling_plan}
          samples={data.samples}
          lineOptions={details.map((d) => ({
            id: d.id,
            label: `${String(d.item_code || 'Item')} · L${String(d.po_line_number ?? '')}`,
          }))}
          onSaved={async () => {
            await refetch();
          }}
        />
      )}

      <Card
        title="Receiving Dimension Inspection"
        size="small"
        style={{ marginBottom: 16 }}
        extra={<ExperimentOutlined />}
      >
        {!dimInitiated ? (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text type="secondary">
              Optional. If you initiate Dimension Inspection, Receiving Pass is blocked until Dimension
              is Passed. If you never initiate, you may Pass Receiving with a recorded bypass reason.
            </Text>
            {canComplete && (
              <Button type="default" loading={initiating} onClick={() => void initiateDimension()}>
                Initiate Dimension Inspection
              </Button>
            )}
          </Space>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small">
              <Descriptions.Item label="Number">{dim?.inspection_number || '—'}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag>{dim?.status || '—'}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Result">
                <Tag
                  color={
                    dim?.result === 'Pass' ? 'green' : dim?.result === 'Fail' ? 'red' : 'default'
                  }
                >
                  {dim?.result || 'Pending'}
                </Tag>
              </Descriptions.Item>
            </Descriptions>
            <Space>
              <Button
                type="link"
                style={{ paddingLeft: 0 }}
                onClick={() =>
                  navigate(`/qms/receiving-dimensional-inspections/${dim?.id || data.dimensional_inspection_id}`)
                }
              >
                Open Dimension Inspection
              </Button>
              {canComplete && dimFailed && (
                <Button loading={initiating} onClick={() => void initiateDimension()}>
                  Re-initiate after Fail
                </Button>
              )}
            </Space>
            {dimInitiated && !dimPassed && (
              <Alert
                type="warning"
                showIcon
                message="Receiving Pass is blocked until Dimension Inspection is Passed"
              />
            )}
          </Space>
        )}
      </Card>

      <Card title="GRN lines" size="small" style={{ marginBottom: 16 }}>
        <Table
          size="small"
          rowKey="id"
          pagination={false}
          dataSource={details}
          expandable={{
            rowExpandable: (r) => (r.heat_lines?.length || 0) > 0,
            expandedRowRender: (r) => (
              <Table
                size="small"
                pagination={false}
                rowKey={(h) => h.id}
                dataSource={r.heat_lines || []}
                columns={[
                  { title: 'Heat number', dataIndex: 'heat_number', width: 140 },
                  {
                    title: 'Dimension',
                    dataIndex: 'dimension',
                    width: 140,
                    render: (v: string) => v || '—',
                  },
                  {
                    title: 'Qty',
                    dataIndex: 'quantity',
                    width: 100,
                    align: 'right',
                    render: (v: number) => Number(v).toLocaleString(),
                  },
                  {
                    title: 'MTC document',
                    key: 'mtc_doc',
                    width: 300,
                    render: (_: unknown, h: HeatLineRow) => (
                      <MtcDocumentLink
                        documentId={h.mtc_document_id}
                        documentNumber={h.mtc_document_number}
                        compact
                      />
                    ),
                  },
                ]}
              />
            ),
          }}
          columns={[
            { title: 'GRN #', dataIndex: 'grn_number', width: 140 },
            { title: 'PO #', dataIndex: 'po_number', width: 120 },
            { title: 'Line', dataIndex: 'po_line_number', width: 70 },
            {
              title: 'Critical',
              key: 'critical',
              width: 90,
              render: (_: unknown, row: ReceivingDetailRow) =>
                String(row.remarks || '').toLowerCase().includes('critical') ? (
                  <Tag color="red">Critical</Tag>
                ) : (
                  <Text type="secondary">—</Text>
                ),
            },
            {
              title: 'Heat / MTC',
              key: 'heat_mtc',
              width: 130,
              render: (_: unknown, row: ReceivingDetailRow) => {
                const n = row.heat_lines?.length || 0;
                return n > 0 ? (
                  <Tag color="blue">
                    {n} heat line{n !== 1 ? 's' : ''} — expand
                  </Tag>
                ) : (
                  <Text type="secondary">—</Text>
                );
              },
            },
            { title: 'Item', dataIndex: 'item_code', width: 120 },
            { title: 'Description', dataIndex: 'item_description', ellipsis: true },
            { title: 'Ordered', dataIndex: 'quantity_ordered', width: 90 },
            { title: 'Received', dataIndex: 'quantity_received', width: 90 },
            { title: 'UOM', dataIndex: 'unit', width: 70 },
            { title: 'Acceptance', dataIndex: 'acceptance_status', width: 120 },
          ]}
        />
      </Card>

      <Card title="Heat lines & MTC (traceability)" size="small">
        {hasMtcGap && heatLines.length > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message={
              <span>
                One or more heat lines are missing an MTC document. Remediate on the{' '}
                <Link to="/scm/quality/critical-mtc">Critical MTC gaps</Link> page or on the GRN
                {data.grn ? (
                  <>
                    {' '}
                    (<Link to={`/scm/procurement/grn/${data.grn.id}`}>open GRN</Link>)
                  </>
                ) : null}
                .
              </span>
            }
          />
        )}
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          Traceability from the linked GRN (same heat/MTC data as the GRN receipt).
        </Text>
        {heatLines.length > 0 ? (
          <Table
            size="small"
            rowKey={(r) => `${r.line_number}-${r.id}`}
            pagination={false}
            scroll={{ x: 'max-content' }}
            dataSource={heatLines}
            columns={[
              { title: 'Line #', dataIndex: 'line_number', width: 70 },
              { title: 'Item', dataIndex: 'item_code', width: 140 },
              {
                title: 'Description',
                dataIndex: 'item_description',
                ellipsis: true,
                width: 200,
                render: (desc: string) => desc || '—',
              },
              { title: 'Heat number', dataIndex: 'heat_number', width: 120 },
              {
                title: 'Dimension',
                dataIndex: 'dimension',
                width: 120,
                render: (v: string) => v || '—',
              },
              {
                title: 'Qty',
                dataIndex: 'quantity',
                width: 90,
                align: 'right',
                render: (v: number) => Number(v).toLocaleString(),
              },
              {
                title: 'MTC document',
                key: 'mtc_doc',
                width: 300,
                render: (_: unknown, r: HeatLineRow) => (
                  <MtcDocumentLink
                    documentId={r.mtc_document_id}
                    documentNumber={r.mtc_document_number}
                    compact
                  />
                ),
              },
            ]}
          />
        ) : (
          <Text type="secondary">No heat lines are stored on the linked GRN.</Text>
        )}
      </Card>

      <Modal
        title={
          completeResult === 'Pass'
            ? dimInitiated
              ? 'Submit Pass for approval'
              : 'Submit Pass for approval (Dimension not initiated)'
            : 'Submit Fail for approval'
        }
        open={completeOpen}
        onCancel={() => setCompleteOpen(false)}
        onOk={() => void submitComplete()}
        confirmLoading={saving}
        okText="Submit for approval"
        okButtonProps={{ danger: completeResult === 'Fail' }}
        width={560}
      >
        {completeResult === 'Pass' && !dimInitiated && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="Receiving Dimension Inspection was not initiated"
            description="You can bypass and submit Pass for approval, but the reason is recorded as evidence."
          />
        )}
        <Form form={form} layout="vertical">
          {completeResult === 'Pass' && !dimInitiated && (
            <Form.Item
              name="dimensional_bypass_reason"
              label="Bypass reason (required)"
              rules={[{ required: true, message: 'Required when Dimension was not initiated' }]}
            >
              <TextArea rows={3} placeholder="Why Dimension Inspection is not required / not done" />
            </Form.Item>
          )}
          <Form.Item
            name="findings"
            label="Findings / notes"
            rules={
              completeResult === 'Fail'
                ? [{ required: true, message: 'Required when failing' }]
                : undefined
            }
          >
            <TextArea rows={4} placeholder="Inspection findings" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ReceivingInspectionDetailPage;
